import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { QuantitySelector } from '../../atoms/QuantitySelector';
import { Spinner } from '../../atoms/Spinner';
import { Icon } from '../../atoms/Icon';
import { Flex } from '../../atoms/Flex';
import { Text } from '../../atoms/Text';
import { Errorbox } from '../../atoms/Errorbox';
import { Link } from '../../atoms/Link';
import { Select } from '../../atoms/Select';
import { Input } from '../../atoms/Input';
import { Button } from '../../atoms/Button';
import { List } from '../../atoms/List';
import { ListItem } from '../../atoms/ListItem';
import { DescriptionList } from '../../atoms/DescriptionList';
import {
  ContactFormModal,
  ContactFormData,
  ContactFormSubmitResult,
  Country,
} from '../../organisms/ContactFormModal';
import './BasketPage.css';

// =============================================================================
// Types
// =============================================================================

interface AlternateProduct {
  min: number;
  max: number;
  step: number;
  is_self?: boolean;
}

interface VpsSpec {
  cpu_cores: number;
  ram_mb: number;
  hdd_mb: number;
  raid_type: string | null;
  os: string | null;
  addon: string | null;
  addon_name: string | null;
  total_price: string;
}

interface ContactData {
  id: string;
  label: string;
  country: string;
  invalid_for_tld: string | null;
}

interface InsufficientCredits {
  uk: string;
  uk_1y: string;
  gtld: string;
  'gtld.transfer': string;
}

export interface BasketItemData {
  name: string;
  current_price: string;
  instances: number;
  multiple_allowed: boolean;
  years: number | 'transfer' | null;
  min_years: number;
  max_years: number;
  years_step: number;
  alternate_products: AlternateProduct[];
  contact: string | null;
  contactCountriesAllowed: Record<string, boolean> | null;
  domain_credits: boolean | 'used' | null;
  authcode: string;
  aot_authcode_supported: boolean;
  keep_nameservers: number;
  completion_fee: string | null;
  extra_days: number | null;
  suppress: boolean;
  target: string | null;
  upgrade_for: string | null;
  os: string | null;
  product_name: string | null;
  qualifying_contact_id: string | null;
  is_vps: boolean;
  vps_ref: string | null;
  vps_spec: VpsSpec | null;
  vps_product_type: string | null;
  other_spec: string[] | null;
  depends_on: string | null;
  count: number;
  capacity_mb_multiplier: number | null;
  inc_setup_fee: boolean;
  is_lrp_registration: boolean;
  item_renewal_id: string | null;
  downgrade_cancel_type: string | null;
  downgrade_cancel_addon_full_name: string | null;
  noDisplay?: boolean;
  _bundleType?: 'bundle-parent' | 'bundle-child';
  _bundleId?: string;
}

interface BasketResponse {
  heldBasket?: boolean;
  basketItems: BasketItemData[];
  allowedContacts: ContactData[];
  domainCreditsAll?: string;
  insufficientCredits?: InsufficientCredits;
}

interface ProcessedItem extends BasketItemData {
  contacts: ContactData[];
  invalidReason?: string;
  isVpsExtra: boolean;
  displayName: string;
  displayPrice: string;
}

export interface EmptyBasketLink {
  id: string;
  caption: string;
  url: string;
  order: number;
}

export interface BasketPageProps {
  /** Base URL for basket API */
  fetchUrl?: string;
  /** Whether to skip the upsell page */
  skipUpsell?: boolean;
  /** Whether there's a VPS upgrade in basket */
  hasVpsUpgrade?: boolean;
  /** Whether some items couldn't be added */
  someItemsMissed?: boolean;
  /** Sales email for error messages */
  salesEmailAddress?: string;
  /** Whether the customer can purchase hybrid hosting */
  canPurchaseHybrid?: boolean;
  /** Product links to show when basket is empty */
  emptyBasketLinks?: EmptyBasketLink[];
  /** Whether customer was redirected due to missing contact */
  missingContactRedirect?: boolean;
  /** Country list for the add-contact modal */
  countries?: Country[];
  /** Async handler for creating a new contact via the modal */
  onContactSubmit?: (data: ContactFormData) => Promise<ContactFormSubmitResult>;
  /** Address finder API key for the add-contact modal */
  addressFinderApiKey?: string;
  /** Custom fetch function (defaults to window.fetch) */
  fetchFn?: typeof window.fetch;
  /** Trustpilot widget token */
  trustpilotToken?: string;
  /** Trustpilot template ID */
  trustpilotTemplateId?: string;
  /** Trustpilot business unit ID */
  trustpilotBusinessUnitId?: string;
  /** Custom CSS class */
  className?: string;
}

// =============================================================================
// Helper Functions
// =============================================================================

function sanitizeId(str: string): string {
  return str.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function sortAndAssignBundleIds(items: BasketItemData[]): BasketItemData[] {
  const vpsParents: Record<string, BasketItemData> = {};
  const vpsChildren: Record<string, BasketItemData[]> = {};
  const hostingParents: Record<string, BasketItemData> = {};
  const hostingChildren: Record<string, BasketItemData[]> = {};
  const others: BasketItemData[] = [];

  items.forEach((item) => {
    if (item.vps_ref && item.is_vps) {
      vpsParents[item.vps_ref] = item;
      item._bundleType = 'bundle-parent';
      item._bundleId = 'bundle-parent-' + sanitizeId(item.vps_ref);
    } else if (item.vps_ref && !item.is_vps) {
      if (!vpsChildren[item.vps_ref]) vpsChildren[item.vps_ref] = [];
      vpsChildren[item.vps_ref].push(item);
      item._bundleType = 'bundle-child';
      item._bundleId = 'bundle-child-' + sanitizeId(item.vps_ref);
    } else if (item.name && !item.depends_on) {
      hostingParents[item.name] = item;
      item._bundleType = 'bundle-parent';
      item._bundleId = 'bundle-parent-' + sanitizeId(item.name);
    } else if (item.depends_on) {
      if (!hostingChildren[item.depends_on]) hostingChildren[item.depends_on] = [];
      hostingChildren[item.depends_on].push(item);
      item._bundleType = 'bundle-child';
      item._bundleId = 'bundle-child-' + sanitizeId(item.depends_on);
    } else {
      others.push(item);
    }
  });

  const sorted: BasketItemData[] = [];
  Object.keys(vpsParents).forEach((ref) => {
    sorted.push(vpsParents[ref]);
    if (vpsChildren[ref]) sorted.push(...vpsChildren[ref]);
  });
  Object.keys(hostingParents).forEach((name) => {
    sorted.push(hostingParents[name]);
    if (hostingChildren[name]) sorted.push(...hostingChildren[name]);
  });
  // Orphaned children
  Object.values(vpsChildren)
    .flat()
    .forEach((c) => {
      if (!sorted.includes(c)) sorted.push(c);
    });
  Object.values(hostingChildren)
    .flat()
    .forEach((c) => {
      if (!sorted.includes(c)) sorted.push(c);
    });
  others.forEach((item) => {
    sorted.push(item);
  });

  return sorted;
}

function cleanDisplayName(item: BasketItemData): string {
  let name = item.name;
  name = name.replace(/ \(ref [^)]+\)/, '');
  name = name.replace(/\(preref [^)]+\)/, '');
  if (item.vps_ref && !item.is_vps) {
    name = name.replace(/^VPS -/, '');
    name = name.replace(/ (Monthly|Yearly)$/, '');
  } else if (item.is_vps) {
    name = name.replace('(base specification) ', '');
  }
  return name.trim();
}

interface YearOption {
  value: number;
  label: string;
  recalculate: boolean;
}

function buildYearOptions(item: BasketItemData): YearOption[] {
  if (!item.years || item.years === 'transfer' || Number(item.years) <= 0) return [];

  const yearsToProduct: Record<number, 's' | 'o'> = {};
  const allProducts = [
    ...(item.alternate_products || []),
    { min: item.min_years, max: item.max_years, step: item.years_step, is_self: true },
  ];

  for (const p of allProducts) {
    for (let i = 1; i <= 10; i++) {
      if (i >= p.min && i % p.step === 0 && i <= p.max) {
        yearsToProduct[i] = (p as { is_self?: boolean }).is_self ? 's' : 'o';
      }
    }
  }

  const options: YearOption[] = [];
  for (let i = 1; i <= 10; i++) {
    if (!yearsToProduct[i]) continue;
    options.push({
      value: i,
      label: `${i} year${i !== 1 ? 's' : ''}${yearsToProduct[i] === 'o' ? '*' : ''}`,
      recalculate: yearsToProduct[i] === 'o',
    });
  }

  return options;
}

function getFilteredContacts(
  item: BasketItemData,
  allContacts: ContactData[],
  displayName: string
): ContactData[] {
  let filtered = item.contactCountriesAllowed
    ? allContacts.filter((c) => item.contactCountriesAllowed![c.country])
    : [...allContacts];

  // Filter by TLD validity
  const tldMatch = displayName.match(/^[^.]*[.](.*)/);
  if (tldMatch) {
    const tld = tldMatch[1];
    const ukMatch = tld.match(/[.](uk)$/);
    const tldForValidation = ukMatch ? 'uk' : tld;
    filtered = filtered.filter((c) => c.invalid_for_tld !== tldForValidation);
  }

  // Restrict to qualifying contact if set
  if (item.qualifying_contact_id) {
    filtered = filtered.filter((c) => c.id === item.qualifying_contact_id);
  }

  return filtered;
}

function shouldShowContactSelect(item: BasketItemData, displayName: string): boolean {
  if (!item.years || item.item_renewal_id) return false;
  if (displayName.match(/[.]uk$/) && item.years === 'transfer') return false;
  return true;
}

function abbreviateMiddle(maxLen: number, str: string): string {
  if (str.length <= maxLen) return str;
  const half = Math.floor((maxLen - 3) / 2);
  return str.substring(0, half) + '...' + str.substring(str.length - half);
}

function setCookie(name: string, value: string, mins: number): void {
  const exp = new Date();
  exp.setTime(exp.getTime() + 1000 * 60 * mins);
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${exp.toUTCString()}`;
}

// =============================================================================
// Component
// =============================================================================

export const BasketPage: React.FC<BasketPageProps> = ({
  fetchUrl = '/manage/basket.cgi',
  skipUpsell = false,
  hasVpsUpgrade = false,
  someItemsMissed = false,
  salesEmailAddress = '',
  canPurchaseHybrid = true,
  emptyBasketLinks = [],
  missingContactRedirect = false,
  countries,
  onContactSubmit,
  addressFinderApiKey,
  fetchFn = window.fetch.bind(window),
  trustpilotToken,
  trustpilotTemplateId,
  trustpilotBusinessUnitId,
  className = '',
}) => {
  const [data, setData] = useState<BasketResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [updatingTotal, setUpdatingTotal] = useState(false);
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalItemName, setContactModalItemName] = useState<string | null>(null);

  const fetchFnRef = useRef(fetchFn);
  const abortRef = useRef<AbortController | null>(null);
  const authcodeTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const loadBasket = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetchFnRef.current(`${fetchUrl}?json=1`, {
        signal: controller.signal,
      });
      const json: BasketResponse = await res.json();
      setData(json);
      setLoading(false);
      setHasLoaded(true);
      setUpdatingTotal(false);
      setUpdatingItems(new Set());
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setLoading(false);
        setHasLoaded(true);
      }
    }
  }, [fetchUrl]);

  useEffect(() => {
    loadBasket();
    return () => {
      abortRef.current?.abort();
    };
  }, [loadBasket]);

  // ---------------------------------------------------------------------------
  // Mutation handler — sends quick update then full reload
  // ---------------------------------------------------------------------------

  const sendMutation = useCallback(
    async (params: Record<string, string>, itemName?: string) => {
      setUpdatingTotal(true);
      if (itemName) {
        setUpdatingItems((prev) => new Set(prev).add(itemName));
      }

      const qs = new URLSearchParams({ json: '1', quick: '1', ...params });
      try {
        await fetchFnRef.current(`${fetchUrl}?${qs}`);
      } catch {
        // continue to reload even on error
      }
      await loadBasket();
    },
    [fetchUrl, loadBasket]
  );

  // ---------------------------------------------------------------------------
  // Process items
  // ---------------------------------------------------------------------------

  const { processedItems, noContactDomains, totalExVat, domainCount } = useMemo(() => {
    if (!data?.basketItems) {
      return {
        processedItems: [] as ProcessedItem[],
        noContactDomains: new Set<string>(),
        totalExVat: 0,
        domainCount: 0,
      };
    }

    const sorted = sortAndAssignBundleIds(data.basketItems);
    const contacts = data.allowedContacts || [];
    const ncd = new Set<string>();
    let total = 0;
    let domains = 0;

    // Hide addon/OS downgrades when matching upgrades exist
    const addonDowngrades = sorted.filter((i) => i.downgrade_cancel_type?.match(/^addon\./));
    const addonUpgrades = sorted.filter((i) => i.vps_product_type === 'addon');
    const osDowngrades = sorted.filter((i) => i.downgrade_cancel_type?.match(/^os\./));
    const osUpgrades = sorted.filter((i) => i.vps_product_type === 'os');
    if (addonDowngrades.length === 1 && addonUpgrades.length === 1)
      addonDowngrades[0].noDisplay = true;
    if (osDowngrades.length === 1 && osUpgrades.length === 1) osDowngrades[0].noDisplay = true;

    const processed: ProcessedItem[] = sorted.map((item) => {
      const displayName = cleanDisplayName(item);
      const filteredContacts = getFilteredContacts(item, contacts, displayName);

      let invalidReason: string | undefined;
      if (item.years) {
        if (contacts.length === 0) {
          invalidReason = 'You must add some contacts to continue.';
        } else if (filteredContacts.length === 0) {
          invalidReason = `You don't have any contacts from the countries required for: ${item.name}, please contact sales for details`;
        }
      }

      if (item.years && item.contact === null && !item.item_renewal_id) {
        ncd.add(item.name);
      }

      const isVpsExtra =
        !item.is_vps &&
        !!item.vps_ref &&
        !!item.vps_product_type &&
        /^(spec|os|addon|downgrade)$/.test(item.vps_product_type);
      const fullPrice = (parseFloat(item.current_price) * item.instances).toFixed(2);

      if (!item.suppress) total += parseFloat(fullPrice);

      let displayPrice = fullPrice;
      if (item.is_vps && item.vps_spec) {
        displayPrice = Number(item.vps_spec.total_price).toFixed(2);
      }

      if (item.years && (item.years === 'transfer' || parseInt(String(item.years)) > 0)) {
        domains++;
      }

      return {
        ...item,
        contacts: filteredContacts,
        invalidReason,
        isVpsExtra,
        displayName,
        displayPrice,
      };
    });

    return {
      processedItems: processed,
      noContactDomains: ncd,
      totalExVat: total,
      domainCount: domains,
    };
  }, [data]);

  // ---------------------------------------------------------------------------
  // Mobile card groups — each group is a parent + its bundle children
  // ---------------------------------------------------------------------------

  const cardGroups = useMemo(() => {
    const groups: { parent: ProcessedItem; children: ProcessedItem[] }[] = [];
    for (const item of processedItems) {
      if (item._bundleType === 'bundle-child' || item.isVpsExtra) {
        const last = groups[groups.length - 1];
        if (last) last.children.push(item);
      } else {
        groups.push({ parent: item, children: [] });
      }
    }
    return groups;
  }, [processedItems]);

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  const handleQuantityChange = useCallback(
    (itemName: string, newQty: number) => {
      sendMutation({ [`instances-${itemName}`]: String(newQty) }, itemName);
    },
    [sendMutation]
  );

  const handleRemoveItem = useCallback(
    (itemName: string) => {
      sendMutation({ submit: 'DELETE', name: itemName }, itemName);
    },
    [sendMutation]
  );

  const handleYearsChange = useCallback(
    (itemName: string, years: string, recalculate: boolean) => {
      const params: Record<string, string> = { 'submit-type': 'SETY', name: itemName, years };
      if (recalculate) params.recalculate = '1';
      sendMutation(params, itemName);
    },
    [sendMutation]
  );

  const handleNameserverChange = useCallback(
    (itemName: string, keepNameservers: string) => {
      sendMutation(
        { 'submit-type': 'SETY', name: itemName, keep_nameservers: keepNameservers },
        itemName
      );
    },
    [sendMutation]
  );

  const handleContactChange = useCallback(
    (itemName: string, contactId: string) => {
      if (contactId === 'add-contact') {
        if (countries && onContactSubmit) {
          setContactModalItemName(itemName);
          setContactModalOpen(true);
        } else {
          window.open('/manage/account-ng.cgi?m=list_contacts', '_blank');
        }
        return;
      }
      sendMutation({ 'submit-type': 'SETY', name: itemName, contact: contactId }, itemName);
    },
    [sendMutation, countries, onContactSubmit]
  );

  const handleContactModalSuccess = useCallback(
    (contactId: string) => {
      setContactModalOpen(false);
      sendMutation(
        { 'submit-type': 'SETY', name: contactModalItemName!, contact: contactId },
        contactModalItemName!
      );
      setContactModalItemName(null);
    },
    [sendMutation, contactModalItemName]
  );

  const handleContactModalClose = useCallback(() => {
    setContactModalOpen(false);
    setContactModalItemName(null);
    loadBasket();
  }, [loadBasket]);

  const handleAuthcodeChange = useCallback(
    (itemName: string, authcode: string) => {
      if (authcodeTimers.current[itemName]) clearTimeout(authcodeTimers.current[itemName]);
      authcodeTimers.current[itemName] = setTimeout(() => {
        sendMutation({ 'submit-type': 'SETY', name: itemName, authcode }, itemName);
      }, 500);
    },
    [sendMutation]
  );

  const handleDomainCreditsChange = useCallback(
    (itemName: string, value: string) => {
      sendMutation(
        { 'submit-type': 'USE_ROBOT_CREDIT', name: itemName, credityesno: value },
        itemName
      );
    },
    [sendMutation]
  );

  const handleDomainCreditsAllChange = useCallback(
    (value: string) => {
      sendMutation({ 'submit-type': 'USE_ROBOT_CREDIT', type: 'ALL', credityesno: value });
    },
    [sendMutation]
  );

  const handleCheckoutSubmit = useCallback(() => {
    setCookie('visited_basket_page_recently', '1', 10);
  }, []);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const checkoutAction = skipUpsell ? '/manage/checkout.cgi' : '/manage/stage.cgi';
  const canCheckout =
    hasLoaded &&
    noContactDomains.size === 0 &&
    !updatingTotal &&
    !hasVpsUpgrade &&
    !data?.insufficientCredits;
  const isHeldBasket = data?.heldBasket === true;
  const isEmpty = hasLoaded && !loading && data && !isHeldBasket && processedItems.length === 0;
  const hasItems = processedItems.length > 0 && !isHeldBasket;
  const sortedLinks = [...emptyBasketLinks]
    .filter((l) => l.id !== 'hybrid' || canPurchaseHybrid)
    .sort((a, b) => a.order - b.order);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={`hi-ui-basket-page ${className}`.trim()}>
      {/* Title */}
      <Flex align="center" gap="sm" className="hi-ui-basket-page__title-row">
        <Icon name="basket" size={28} className="hi-ui-basket-page__icon" aria-label="Basket" />
        <Text variant="heading-1" inline className="hi-ui-basket-page__title">
          Basket
        </Text>
      </Flex>

      {/* Main section */}
      <div className="hi-ui-basket-page__section">
        {/* Error: VPS upgrade incomplete */}
        {hasVpsUpgrade && (
          <Errorbox className="hi-ui-basket-page__error">
            Your basket currently contains an incomplete VPS upgrade.
          </Errorbox>
        )}

        {/* Error: some items missed */}
        {someItemsMissed && (
          <Errorbox className="hi-ui-basket-page__error">
            Some items could not be added. Please contact sales at {salesEmailAddress}.
          </Errorbox>
        )}

        {/* Error: missing contacts redirect */}
        {missingContactRedirect && noContactDomains.size > 0 && (
          <Errorbox className="hi-ui-basket-page__error">
            Please add an owner for each of your domains.
          </Errorbox>
        )}

        {/* Error: no contact selected */}
        {noContactDomains.size > 0 && hasItems && (
          <Errorbox className="hi-ui-basket-page__error">
            Please select an owner for each domain
          </Errorbox>
        )}

        {/* Insufficient credits warning */}
        {data?.insufficientCredits && (
          <div className="hi-ui-basket-page__credit-warning">
            <Errorbox className="hi-ui-basket-page__error">
              You do not have enough credits to complete this transaction. Either{' '}
              <Link href="/manage/order-domain-credits.cgi">purchase some more credits</Link> or
              change the payment method for some of your domain names.
            </Errorbox>
            <div className="hi-ui-basket-page__credits-table">
              <Text bold className="hi-ui-basket-page__credits-intro">
                Current credits:
              </Text>
              <DescriptionList
                layout="horizontal"
                size="sm"
                className="hi-ui-basket-page__credits-list"
                items={[
                  { label: 'UK Domain Credits (.uk, .co.uk):', value: data.insufficientCredits.uk },
                  {
                    label: 'UK Single-year Domain Credits (.uk, .co.uk):',
                    value: data.insufficientCredits.uk_1y,
                  },
                  {
                    label: 'gTLD Domain Credits (.com, .net, .org, etc.):',
                    value: data.insufficientCredits.gtld,
                  },
                  {
                    label: 'gTLD Transfer-in Credits:',
                    value: data.insufficientCredits['gtld.transfer'],
                  },
                ].filter((item) => Number(item.value) > 0)}
              />
            </div>
          </div>
        )}

        {/* Held basket */}
        {isHeldBasket && (
          <Errorbox className="hi-ui-basket-page__error">
            You have an incomplete purchase in progress. Please{' '}
            <Link href="/manage/pending-purchase.cgi">click here</Link> to complete it.
          </Errorbox>
        )}

        {/* Loading state */}
        {loading && !hasLoaded && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="md"
            className="hi-ui-basket-page__loading-container"
          >
            <Spinner size="medium" aria-label="Loading your basket" />
            <Text>Loading your basket...</Text>
          </Flex>
        )}

        {/* Empty basket */}
        {isEmpty && (
          <div className="hi-ui-basket-page__empty">
            <Text variant="heading-3" inline className="hi-ui-basket-page__empty-title">
              Your basket is empty.
            </Text>
            {sortedLinks.length > 0 && (
              <>
                <Text className="hi-ui-basket-page__empty-prompt">
                  Check out these products to get started:
                </Text>
                <List listStyle="disc" className="hi-ui-basket-page__empty-list">
                  {sortedLinks.map((link) => (
                    <ListItem key={link.id} className="hi-ui-basket-page__empty-list-item">
                      <Link
                        className="hi-ui-basket-page__empty-link"
                        href={link.url}
                        onClick={() => setCookie('visited_basket_page_recently', '1', 10)}
                      >
                        {link.caption}
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </div>
        )}

        {/* Basket table */}
        {hasItems && !hasVpsUpgrade && (
          <>
            {/* Desktop table view */}
            <div className="hi-ui-basket-page__desktop">
              <table className="hi-ui-basket-page__table">
                <thead className="hi-ui-basket-page__thead">
                  <tr>
                    <th className="hi-ui-basket-page__th-product">Product</th>
                    <th className="hi-ui-basket-page__th-qty">Qty</th>
                    <th className="hi-ui-basket-page__th-price">Price</th>
                    <th className="hi-ui-basket-page__th-delete" />
                  </tr>
                  <tr>
                    <td colSpan={4} className="hi-ui-basket-page__gradient-bar" />
                  </tr>
                </thead>
                <tbody>
                  {processedItems.map((item) => {
                    const isBundleChild = item._bundleType === 'bundle-child';
                    const isHiddenVpsExtra =
                      item.isVpsExtra &&
                      item.vps_product_type &&
                      /^(spec|os|addon)$/.test(item.vps_product_type);
                    const rowHidden = item.noDisplay || isHiddenVpsExtra;
                    const rowClass =
                      isBundleChild || item.isVpsExtra
                        ? 'hi-ui-basket-page__row-child'
                        : 'hi-ui-basket-page__row';
                    const yearOptions = buildYearOptions(item);
                    const showContactSelect =
                      !item.suppress && shouldShowContactSelect(item, item.displayName);
                    const showQtySelector =
                      item.multiple_allowed && !item.suppress && !item.isVpsExtra && !isBundleChild;

                    return (
                      <tr
                        key={item.name}
                        className={rowClass}
                        style={rowHidden ? { display: 'none' } : undefined}
                        data-bundle-id={item._bundleId}
                        data-bundle-type={item._bundleType}
                      >
                        {/* Product cell */}
                        <td className="hi-ui-basket-page__td-product">
                          <div className="hi-ui-basket-page__product-inner">
                            {/* VPS product type prefix */}
                            {item.vps_product_type === 'os' && (
                              <Text variant="inline" bold>
                                OS:{' '}
                              </Text>
                            )}
                            {item.vps_product_type === 'addon' && (
                              <Text variant="inline" bold>
                                Control panel:{' '}
                              </Text>
                            )}

                            {/* Product name */}
                            <Text
                              variant="inline"
                              className="hi-ui-basket-page__item-title"
                              style={item.suppress ? { textDecoration: 'line-through' } : undefined}
                            >
                              {item.displayName}
                            </Text>

                            {/* Target server */}
                            {item.target && (
                              <Text variant="inline" className="hi-ui-basket-page__item-category">
                                {' '}
                                ({item.target})
                              </Text>
                            )}

                            {/* Upgrade info */}
                            {item.upgrade_for &&
                              isNaN(Number(item.upgrade_for)) &&
                              (!item.vps_ref || item.upgrade_for !== item.vps_ref) && (
                                <> (upgrade for {item.upgrade_for})</>
                              )}

                            {/* Extra days */}
                            {!!item.extra_days && <> extended by {item.extra_days} days</>}

                            {/* Setup fee */}
                            {item.inc_setup_fee && (
                              <div className="hi-ui-basket-page__product-detail">
                                + Includes set-up fee
                              </div>
                            )}

                            {/* Capacity */}
                            {!!item.capacity_mb_multiplier && (
                              <Text variant="inline" bold>
                                {' '}
                                (Capacity {(item.capacity_mb_multiplier * item.instances) /
                                  1024}{' '}
                                GB)
                              </Text>
                            )}

                            {/* Product name label */}
                            {item.product_name &&
                              !item.vps_ref &&
                              !(
                                item.product_name.match(/1.year/) &&
                                item.displayName.match(/[.]uk$/)
                              ) && (
                                <Text variant="inline" className="hi-ui-basket-page__item-category">
                                  {' '}
                                  ({item.product_name})
                                </Text>
                              )}

                            {/* --- Domain-specific sections (only when not suppressed) --- */}
                            {!item.suppress && (
                              <>
                                {/* Years select (domain registration) */}
                                {item.years &&
                                  item.years !== 'transfer' &&
                                  Number(item.years) > 0 &&
                                  yearOptions.length > 1 && (
                                    <>
                                      {' '}
                                      <Select
                                        size="sm"
                                        className="hi-ui-basket-page__select"
                                        value={String(item.years)}
                                        onChange={(e) => {
                                          const opt = yearOptions.find(
                                            (o) => String(o.value) === e.target.value
                                          );
                                          handleYearsChange(
                                            item.name,
                                            e.target.value,
                                            opt?.recalculate || false
                                          );
                                        }}
                                      >
                                        {yearOptions.map((opt) => (
                                          <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </option>
                                        ))}
                                      </Select>
                                    </>
                                  )}

                                {/* Years text (single option) */}
                                {item.years &&
                                  item.years !== 'transfer' &&
                                  Number(item.years) > 0 &&
                                  yearOptions.length === 1 && (
                                    <>
                                      {' '}
                                      {item.years} year{Number(item.years) !== 1 ? 's' : ''}
                                    </>
                                  )}

                                {/* Transfer: nameserver select */}
                                {item.years === 'transfer' && (
                                  <>
                                    {' '}
                                    <Select
                                      size="sm"
                                      className="hi-ui-basket-page__select"
                                      value={String(item.keep_nameservers)}
                                      onChange={(e) =>
                                        handleNameserverChange(item.name, e.target.value)
                                      }
                                    >
                                      <option value="0">Change Nameservers</option>
                                      <option value="1">Keep Nameservers</option>
                                    </Select>
                                  </>
                                )}

                                {/* .scot LRP admin fee */}
                                {item.years &&
                                  item.displayName.match(/[.]scot$/) &&
                                  item.is_lrp_registration && (
                                    <Text variant="inline" className="hi-ui-basket-page__admin-fee">
                                      * Includes &pound;5 admin fee
                                    </Text>
                                  )}

                                {/* Back-order completion fee */}
                                {item.completion_fee && (
                                  <div className="hi-ui-basket-page__product-detail">
                                    Back-order: &pound;{item.current_price} (&pound;
                                    {item.completion_fee} + 1 year charge on success)
                                  </div>
                                )}

                                {/* Contact select */}
                                {showContactSelect && (
                                  <div className="hi-ui-basket-page__product-detail">
                                    <Text
                                      variant="inline"
                                      className="hi-ui-basket-page__item-category"
                                    >
                                      Domain owner:{' '}
                                    </Text>
                                    <Select
                                      size="sm"
                                      className="hi-ui-basket-page__select"
                                      value={item.contact || ''}
                                      onChange={(e) =>
                                        handleContactChange(item.name, e.target.value)
                                      }
                                    >
                                      <option value="">- Please select -</option>
                                      <option value="add-contact">- Add new contact -</option>
                                      {item.contacts.map((c) => (
                                        <option key={c.id} value={c.id}>
                                          {abbreviateMiddle(40, c.label)}
                                        </option>
                                      ))}
                                    </Select>
                                  </div>
                                )}

                                {/* Authcode input (transfers) */}
                                {item.years === 'transfer' && item.aot_authcode_supported && (
                                  <div className="hi-ui-basket-page__product-detail">
                                    Authcode:{' '}
                                    <Input
                                      size="sm"
                                      className="hi-ui-basket-page__authcode-input"
                                      defaultValue={item.authcode}
                                      onChange={(e) =>
                                        handleAuthcodeChange(item.name, e.target.value)
                                      }
                                    />
                                  </div>
                                )}

                                {/* Domain credits toggle */}
                                {item.years && item.domain_credits && (
                                  <div className="hi-ui-basket-page__product-detail">
                                    Pay with{' '}
                                    <Link href="/manage/order-domain-credits.cgi" external>
                                      domain credits
                                    </Link>
                                    ?
                                    <Select
                                      size="sm"
                                      className="hi-ui-basket-page__select"
                                      value={
                                        item.domain_credits === 'used'
                                          ? 'use_robot_credit_yes'
                                          : 'use_robot_credit_no'
                                      }
                                      onChange={(e) =>
                                        handleDomainCreditsChange(item.name, e.target.value)
                                      }
                                    >
                                      <option value="use_robot_credit_no">No</option>
                                      <option value="use_robot_credit_yes">Yes</option>
                                    </Select>
                                  </div>
                                )}
                              </>
                            )}

                            {/* VPS spec */}
                            {item.vps_spec && (
                              <>
                                <div className="hi-ui-basket-page__product-detail">
                                  <Text
                                    variant="inline"
                                    className="hi-ui-basket-page__item-category"
                                  >
                                    {item.upgrade_for
                                      ? 'Upgrading to: '
                                      : 'Configured specification: '}
                                  </Text>
                                  <Text variant="inline" className="hi-ui-basket-page__item-spec">
                                    {item.vps_spec.cpu_cores} core
                                    {item.vps_spec.cpu_cores !== 1 ? 's' : ''}
                                    {' / '}
                                    {Math.round((item.vps_spec.ram_mb / 1024) * 100) / 100}GB RAM
                                    {' / '}
                                    {!item.vps_spec.raid_type || item.vps_spec.raid_type === 'none'
                                      ? `${Math.round((item.vps_spec.hdd_mb / 1024) * 100) / 100}GB ${item.name.match(/SSD/) ? 'SSD' : 'HDD'}`
                                      : `RAID ${item.vps_spec.raid_type.replace(/^raid/, '')}`}
                                  </Text>
                                </div>
                                {item.vps_spec.os && (
                                  <div className="hi-ui-basket-page__product-detail">
                                    {item.upgrade_for
                                      ? 'New Operating System: '
                                      : 'Operating System: '}
                                    <Text variant="inline" className="hi-ui-basket-page__item-spec">
                                      {item.vps_spec.os}
                                    </Text>
                                  </div>
                                )}
                                {item.vps_spec.addon && (
                                  <div className="hi-ui-basket-page__product-detail">
                                    {item.upgrade_for ? 'New Control Panel: ' : 'Control Panel: '}
                                    <Text variant="inline">{item.vps_spec.addon}</Text>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Other spec (hosting etc.) */}
                            {!item.vps_spec && item.other_spec && item.other_spec.length > 0 && (
                              <div className="hi-ui-basket-page__product-detail">
                                <Text variant="inline" className="hi-ui-basket-page__item-category">
                                  {item.upgrade_for
                                    ? 'Upgrading to: '
                                    : 'Configured specification: '}
                                </Text>
                                <Text variant="inline" className="hi-ui-basket-page__item-spec">
                                  {item.other_spec.join(' / ')}
                                </Text>
                              </div>
                            )}

                            {/* OS label */}
                            {item.os && (
                              <Text variant="inline" className="hi-ui-basket-page__item-category">
                                {' '}
                                ({item.os})
                              </Text>
                            )}
                          </div>
                        </td>

                        {/* Quantity cell */}
                        <td className="hi-ui-basket-page__td-qty">
                          {showQtySelector ? (
                            <QuantitySelector
                              value={item.instances}
                              onChange={(qty) => handleQuantityChange(item.name, qty)}
                              min={1}
                              aria-label={`Quantity for ${item.displayName}`}
                            />
                          ) : (
                            <Text variant="inline" className="hi-ui-basket-page__qty-static">
                              {item.instances}
                            </Text>
                          )}
                        </td>

                        {/* Price cell */}
                        <td
                          className="hi-ui-basket-page__td-price"
                          style={item.suppress ? { textDecoration: 'line-through' } : undefined}
                        >
                          {updatingItems.has(item.name) ? (
                            <Spinner size="small" />
                          ) : item.domain_credits === 'used' ? (
                            item.count === 1 ? (
                              '1 credit'
                            ) : (
                              `${item.count} credits`
                            )
                          ) : (
                            `\u00A3${item.displayPrice}`
                          )}
                        </td>

                        {/* Delete cell */}
                        <td className="hi-ui-basket-page__td-delete">
                          {!isBundleChild && !item.isVpsExtra && (
                            <button
                              type="button"
                              className="hi-ui-basket-page__delete-btn"
                              onClick={() => handleRemoveItem(item.name)}
                              aria-label={`Remove ${item.displayName}`}
                            >
                              <Icon name="delete-circle" size={19} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="hi-ui-basket-page__total-label" colSpan={2}>
                      Total:
                    </td>
                    <td className="hi-ui-basket-page__total-value" colSpan={2}>
                      {updatingTotal ? <Spinner size="small" /> : `\u00A3${totalExVat.toFixed(2)}`}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Mobile card view */}
            <div className="hi-ui-basket-page__mobile">
              {cardGroups.map((group) => {
                const item = group.parent;
                if (item.noDisplay) return null;

                const showContact =
                  !item.suppress && shouldShowContactSelect(item, item.displayName);
                const yearOpts = buildYearOptions(item);
                const showQty = item.multiple_allowed && !item.suppress;
                const visibleChildren = group.children.filter(
                  (c) =>
                    !c.noDisplay &&
                    !(
                      c.isVpsExtra &&
                      c.vps_product_type &&
                      /^(spec|os|addon)$/.test(c.vps_product_type)
                    )
                );

                return (
                  <article key={item.name} className="hi-ui-basket-page__card">
                    {/* Card header: product name + delete */}
                    <div className="hi-ui-basket-page__card-header">
                      <div>
                        <Text
                          variant="inline"
                          className="hi-ui-basket-page__item-title"
                          style={item.suppress ? { textDecoration: 'line-through' } : undefined}
                        >
                          {item.displayName}
                        </Text>
                        {item.target && (
                          <Text variant="inline" className="hi-ui-basket-page__item-category">
                            {' '}
                            ({item.target})
                          </Text>
                        )}
                        {item.upgrade_for &&
                          isNaN(Number(item.upgrade_for)) &&
                          (!item.vps_ref || item.upgrade_for !== item.vps_ref) && (
                            <> (upgrade for {item.upgrade_for})</>
                          )}
                        {!!item.extra_days && <> extended by {item.extra_days} days</>}
                        {item.product_name &&
                          !item.vps_ref &&
                          !(
                            item.product_name.match(/1.year/) && item.displayName.match(/[.]uk$/)
                          ) && (
                            <Text variant="inline" className="hi-ui-basket-page__item-category">
                              {' '}
                              ({item.product_name})
                            </Text>
                          )}
                      </div>
                      <button
                        type="button"
                        className="hi-ui-basket-page__delete-btn"
                        onClick={() => handleRemoveItem(item.name)}
                        aria-label={`Remove ${item.displayName}`}
                      >
                        <Icon name="delete-circle" size={19} />
                      </button>
                    </div>

                    {/* Card body: details */}
                    <div className="hi-ui-basket-page__card-body">
                      {item.inc_setup_fee && (
                        <div className="hi-ui-basket-page__product-detail">
                          + Includes set-up fee
                        </div>
                      )}
                      {!!item.capacity_mb_multiplier && (
                        <Text variant="inline" bold>
                          Capacity {(item.capacity_mb_multiplier * item.instances) / 1024} GB
                        </Text>
                      )}

                      {/* VPS spec */}
                      {item.vps_spec && (
                        <>
                          <div className="hi-ui-basket-page__product-detail">
                            <Text variant="inline" className="hi-ui-basket-page__item-category">
                              {item.upgrade_for ? 'Upgrading to: ' : 'Configured specification: '}
                            </Text>
                            <Text variant="inline" className="hi-ui-basket-page__item-spec">
                              {item.vps_spec.cpu_cores} core
                              {item.vps_spec.cpu_cores !== 1 ? 's' : ''}
                              {' / '}
                              {Math.round((item.vps_spec.ram_mb / 1024) * 100) / 100}GB RAM
                              {' / '}
                              {!item.vps_spec.raid_type || item.vps_spec.raid_type === 'none'
                                ? `${Math.round((item.vps_spec.hdd_mb / 1024) * 100) / 100}GB ${item.name.match(/SSD/) ? 'SSD' : 'HDD'}`
                                : `RAID ${item.vps_spec.raid_type.replace(/^raid/, '')}`}
                            </Text>
                          </div>
                          {item.vps_spec.os && (
                            <div className="hi-ui-basket-page__product-detail">
                              {item.upgrade_for ? 'New Operating System: ' : 'Operating System: '}
                              <Text variant="inline" className="hi-ui-basket-page__item-spec">
                                {item.vps_spec.os}
                              </Text>
                            </div>
                          )}
                          {item.vps_spec.addon && (
                            <div className="hi-ui-basket-page__product-detail">
                              {item.upgrade_for ? 'New Control Panel: ' : 'Control Panel: '}
                              <Text variant="inline">{item.vps_spec.addon}</Text>
                            </div>
                          )}
                        </>
                      )}

                      {/* Other spec */}
                      {!item.vps_spec && item.other_spec && item.other_spec.length > 0 && (
                        <div className="hi-ui-basket-page__product-detail">
                          <Text variant="inline" className="hi-ui-basket-page__item-category">
                            {item.upgrade_for ? 'Upgrading to: ' : 'Configured specification: '}
                          </Text>
                          <Text variant="inline" className="hi-ui-basket-page__item-spec">
                            {item.other_spec.join(' / ')}
                          </Text>
                        </div>
                      )}

                      {item.os && (
                        <Text variant="inline" className="hi-ui-basket-page__item-category">
                          ({item.os})
                        </Text>
                      )}

                      {/* Domain-specific sections */}
                      {!item.suppress && (
                        <>
                          {item.years &&
                            item.years !== 'transfer' &&
                            Number(item.years) > 0 &&
                            yearOpts.length > 1 && (
                              <div className="hi-ui-basket-page__product-detail">
                                <Select
                                  size="sm"
                                  className="hi-ui-basket-page__select"
                                  value={String(item.years)}
                                  onChange={(e) => {
                                    const opt = yearOpts.find(
                                      (o) => String(o.value) === e.target.value
                                    );
                                    handleYearsChange(
                                      item.name,
                                      e.target.value,
                                      opt?.recalculate || false
                                    );
                                  }}
                                >
                                  {yearOpts.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                            )}
                          {item.years &&
                            item.years !== 'transfer' &&
                            Number(item.years) > 0 &&
                            yearOpts.length === 1 && (
                              <div className="hi-ui-basket-page__product-detail">
                                {item.years} year{Number(item.years) !== 1 ? 's' : ''}
                              </div>
                            )}

                          {item.years === 'transfer' && (
                            <div className="hi-ui-basket-page__product-detail">
                              <Select
                                size="sm"
                                className="hi-ui-basket-page__select"
                                value={String(item.keep_nameservers)}
                                onChange={(e) => handleNameserverChange(item.name, e.target.value)}
                              >
                                <option value="0">Change Nameservers</option>
                                <option value="1">Keep Nameservers</option>
                              </Select>
                            </div>
                          )}

                          {item.years &&
                            item.displayName.match(/[.]scot$/) &&
                            item.is_lrp_registration && (
                              <Text variant="inline" className="hi-ui-basket-page__admin-fee">
                                * Includes &pound;5 admin fee
                              </Text>
                            )}

                          {item.completion_fee && (
                            <div className="hi-ui-basket-page__product-detail">
                              Back-order: &pound;{item.current_price} (&pound;{item.completion_fee}{' '}
                              + 1 year charge on success)
                            </div>
                          )}

                          {showContact && (
                            <div className="hi-ui-basket-page__product-detail">
                              <Text variant="inline" className="hi-ui-basket-page__item-category">
                                Domain owner:{' '}
                              </Text>
                              <Select
                                size="sm"
                                className="hi-ui-basket-page__select"
                                value={item.contact || ''}
                                onChange={(e) => handleContactChange(item.name, e.target.value)}
                              >
                                <option value="">- Please select -</option>
                                <option value="add-contact">- Add new contact -</option>
                                {item.contacts.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {abbreviateMiddle(40, c.label)}
                                  </option>
                                ))}
                              </Select>
                            </div>
                          )}

                          {item.years === 'transfer' && item.aot_authcode_supported && (
                            <div className="hi-ui-basket-page__product-detail">
                              Authcode:{' '}
                              <Input
                                size="sm"
                                className="hi-ui-basket-page__authcode-input"
                                defaultValue={item.authcode}
                                onChange={(e) => handleAuthcodeChange(item.name, e.target.value)}
                              />
                            </div>
                          )}

                          {item.years && item.domain_credits && (
                            <div className="hi-ui-basket-page__product-detail">
                              Pay with{' '}
                              <Link href="/manage/order-domain-credits.cgi" external>
                                domain credits
                              </Link>
                              ?
                              <Select
                                size="sm"
                                className="hi-ui-basket-page__select"
                                value={
                                  item.domain_credits === 'used'
                                    ? 'use_robot_credit_yes'
                                    : 'use_robot_credit_no'
                                }
                                onChange={(e) =>
                                  handleDomainCreditsChange(item.name, e.target.value)
                                }
                              >
                                <option value="use_robot_credit_no">No</option>
                                <option value="use_robot_credit_yes">Yes</option>
                              </Select>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Bundle children */}
                    {visibleChildren.length > 0 && (
                      <div className="hi-ui-basket-page__card-children">
                        {visibleChildren.map((child) => (
                          <div key={child.name} className="hi-ui-basket-page__card-child">
                            <Text variant="inline">{child.displayName}</Text>
                            <Text variant="inline">&pound;{child.displayPrice}</Text>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Card footer: qty + price */}
                    <div className="hi-ui-basket-page__card-footer">
                      <div className="hi-ui-basket-page__card-footer-qty">
                        {showQty ? (
                          <QuantitySelector
                            value={item.instances}
                            onChange={(qty) => handleQuantityChange(item.name, qty)}
                            min={1}
                            aria-label={`Quantity for ${item.displayName}`}
                          />
                        ) : item.instances > 1 ? (
                          <Text variant="inline" className="hi-ui-basket-page__qty-static">
                            {item.instances}
                          </Text>
                        ) : null}
                      </div>
                      <Text
                        variant="inline"
                        className="hi-ui-basket-page__card-footer-price"
                        style={item.suppress ? { textDecoration: 'line-through' } : undefined}
                      >
                        {updatingItems.has(item.name) ? (
                          <Spinner size="small" />
                        ) : item.domain_credits === 'used' ? (
                          item.count === 1 ? (
                            '1 credit'
                          ) : (
                            `${item.count} credits`
                          )
                        ) : (
                          `\u00A3${item.displayPrice}`
                        )}
                      </Text>
                    </div>
                  </article>
                );
              })}

              {/* Mobile total */}
              <div className="hi-ui-basket-page__card-total">
                <Text variant="inline" bold>
                  Total:
                </Text>
                <Text variant="inline" bold className="hi-ui-basket-page__card-total-value">
                  {updatingTotal ? <Spinner size="small" /> : `\u00A3${totalExVat.toFixed(2)}`}
                </Text>
              </div>
            </div>

            <div className="hi-ui-basket-page__bottom">
              {/* Footer prompts */}
              {(domainCount > 0 || data?.domainCreditsAll) && (
                <div className="hi-ui-basket-page__footer-prompts">
                  {domainCount > 0 && (
                    <div>
                      To add or update domain contact details please{' '}
                      <Link
                        href="/manage/account-ng.cgi?m=list_contacts"
                        onClick={() => setCookie('visited_basket_page_recently', '1', 10)}
                      >
                        click here
                      </Link>
                      .
                    </div>
                  )}
                  {data?.domainCreditsAll && (
                    <div>
                      Buy all new domains using{' '}
                      <Link href="/manage/order-domain-credits.cgi" external>
                        domain credits
                      </Link>
                      ?{' '}
                      <Select
                        size="sm"
                        className="hi-ui-basket-page__select"
                        value={
                          parseInt(data.domainCreditsAll)
                            ? 'use_robot_credit_yes'
                            : 'use_robot_credit_no'
                        }
                        onChange={(e) => handleDomainCreditsAllChange(e.target.value)}
                      >
                        <option value="use_robot_credit_no">No</option>
                        <option value="use_robot_credit_yes">Yes</option>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Checkout form */}
              <form method="post" action={checkoutAction} onSubmit={handleCheckoutSubmit}>
                <Input type="hidden" name="submit" value="CHECKOUT" />
                <Flex
                  align="center"
                  justify="end"
                  gap="lg"
                  className="hi-ui-basket-page__checkout-row"
                >
                  <Flex align="center" gap="sm" className="hi-ui-basket-page__checkout-cards">
                    <Icon name="visa" size={49} style={{ height: '31px' }} aria-label="Visa" />
                    <Icon
                      name="mastercard"
                      size={48}
                      style={{ height: '31px' }}
                      aria-label="Mastercard"
                    />
                  </Flex>
                  <Button
                    type="submit"
                    size="large"
                    className="hi-ui-basket-page__checkout-btn"
                    disabled={!canCheckout}
                  >
                    Checkout Now
                    <Icon
                      name="lock-check"
                      size={17}
                      style={{ height: '20px' }}
                      className="hi-ui-basket-page__checkout-lock"
                    />
                  </Button>
                </Flex>
              </form>

              {/* Trustpilot widget — call loadFromElement when the div mounts */}
              {trustpilotToken && (
                <Flex justify="end" className="hi-ui-basket-page__trustpilot-row">
                  <div
                    ref={(el) => {
                      // Trustpilot bootstrap scans on load, but this div is rendered
                      // after React fetches data — manually trigger widget init
                      const tp = (window as unknown as Record<string, unknown>).Trustpilot as
                        | { loadFromElement: (el: HTMLElement) => void }
                        | undefined;
                      if (el && tp?.loadFromElement) {
                        tp.loadFromElement(el);
                      }
                    }}
                    className="trustpilot-widget"
                    data-locale="en-US"
                    data-template-id={trustpilotTemplateId}
                    data-businessunit-id={trustpilotBusinessUnitId}
                    data-style-height="24px"
                    data-style-width="100%"
                    data-token={trustpilotToken}
                    data-min-review-count="0"
                    data-style-alignment="center"
                  >
                    <a
                      href="https://www.trustpilot.com/review/heartinternet.uk"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'none' }}
                    >
                      Trustpilot
                    </a>
                  </div>
                </Flex>
              )}
            </div>
          </>
        )}
      </div>

      {countries && onContactSubmit && (
        <ContactFormModal
          isOpen={contactModalOpen}
          onClose={handleContactModalClose}
          onSuccess={handleContactModalSuccess}
          onSubmit={onContactSubmit}
          countries={countries}
          addressFinderApiKey={addressFinderApiKey}
        />
      )}
    </div>
  );
};
